import React from 'react';
import { LauncherTypes } from '@gamiphy/bl/theme';
import { PopupsStore } from '@gamiphy/loyalty-station-desktop/store/popups';
import { ThemeStore } from '@gamiphy/loyalty-station-desktop/store/theme';
import { UiStore } from '@gamiphy/loyalty-station-desktop/store/ui';
import { BarStore } from '@gamiphy/loyalty-station-desktop/store/ui/bar';
import { MainStore } from '@gamiphy/loyalty-station-desktop/store/ui/main';
import {
  Box, Fade, Grid,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { observer } from 'mobx-react';
import { FLOATING_ICON_OFFSET_X, FLOATING_ICON_OFFSET_Y } from '../frames/floatingIcon/constants';
import { CashbackPopupsStore } from '../store/cashbackPopups';

const BarFrame = React.lazy(() => import('../frames/bar'));
const MainFrame = React.lazy(() => import('../frames/main'));
const GameFrame = React.lazy(() => import('../frames/game'));
const PopupFrame = React.lazy(() => import('../frames/popup'));
const FloatingIconFrame = React.lazy(() => import('../frames/floatingIcon'));
const WelcomeGiftNotificationFrame = React.lazy(() => import('../frames/welcomeGiftNotification'));
const CashbackPopupFrame = React.lazy(() => import('../frames/cashbackPopup'));
const CashbackExampleFrame = React.lazy(() => import('../frames/checkoutExamplePopup'));

const Layout: React.FC = observer(() => {
  const theme = useTheme();
  const barOffset = MainStore.dimensions?.width ?? 0;
  const barHeight = BarStore.dimensions?.height ?? 0;
  const popupOpened = PopupsStore.open;
  const open = UiStore.opened;
  const launcherType = ThemeStore.theme.launcher.type.desktop;
  const size = ThemeStore.iconSize;

  return (
    <Grid>
      {
        UiStore.widgetVisible && (
          <>
            <Box sx={{
              height: '100%',
              width: 328,
              zIndex: 2147483647,
              position: 'fixed',
              bottom: 0,
              top: 0,
              // eslint-disable-next-line no-nested-ternary
              transform: `translateX(${open ? 0 : theme.direction === 'rtl' ? '100%' : '-100%'})`,
              right: theme.direction === 'rtl' ? 0 : 'unset',
              left: theme.direction === 'rtl' ? 'unset' : 0,
              transition: `transform 400ms ${theme.transitions.easing.easeIn}`,
            }}
            >
              <MainFrame />
            </Box>
            {
              !UiStore.barHidden && (
                <Box sx={{
                  borderRadius: 10,
                  maxHeight: '224px',
                  width: '40px',
                  zIndex: 2147483646,
                  position: 'fixed',
                  right: theme.direction === 'rtl' ? 25 : 'unset',
                  left: theme.direction === 'rtl' ? 'unset' : 0,
                  bottom: 0,
                  visibility: (!open && launcherType === LauncherTypes.bar ? 'visible' : 'hidden'),
                  top: `calc(50% - ${barHeight / 2}px)`,
                }}
                >
                  <BarFrame />
                </Box>
              )
            }
            <Box sx={{
              display: popupOpened ? 'block' : 'none',
              zIndex: popupOpened ? 2147483648 : -1,
              position: 'fixed',
              height: '100%',
              width: '100%',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
            }}
            >
              <PopupFrame />
            </Box>
            {
              UiStore.showGame && (
                <Box sx={{
                  zIndex: 2147483647,
                  position: 'fixed',
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  top: 0,
                  bottom: 0,
                  right: 0,
                  left: barOffset,
                }}
                >
                  <GameFrame />
                </Box>
              )
            }
            {
              !UiStore.barHidden && (
                <Fade
                  timeout={1000}
                  in={ThemeStore.theme.launcher.type.desktop === LauncherTypes.floatingIcon && !UiStore.opened}
                >
                  <Box sx={{
                    position: 'fixed',
                    width: size,
                    height: size,
                    right: theme.direction === 'rtl' ? FLOATING_ICON_OFFSET_X : 'unset',
                    left: theme.direction === 'rtl' ? 'unset' : FLOATING_ICON_OFFSET_X,
                    bottom: FLOATING_ICON_OFFSET_Y,
                    zIndex: 2147483647,
                    '&:hover': {
                      width: 200,
                    },
                  }}
                  >
                    <FloatingIconFrame />
                  </Box>
                </Fade>
              )
            }
            {
              !UiStore.barHidden && (
                <Box sx={{
                  display: UiStore.showWelcomeGiftNotification ? 'flex' : 'none',
                  zIndex: 2147483648,
                  position: 'fixed',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
                  maxWidth: 360,
                  width: `calc(100% - ${FLOATING_ICON_OFFSET_X * 2}px)`,
                  ...(launcherType === LauncherTypes.floatingIcon && {
                    bottom: FLOATING_ICON_OFFSET_Y + size + 20,
                    left: FLOATING_ICON_OFFSET_X,
                  }),
                  ...(launcherType === LauncherTypes.bar && {
                    left: `calc(${BarStore.dimensions?.width ?? 0}px + 30px)`,
                    top: '50%',
                    transform: 'translateY(-50%)',
                  }),
                }}
                >
                  <WelcomeGiftNotificationFrame />
                </Box>
              )
            }
          </>
        )
      }
      <Box sx={{
        display: CashbackPopupsStore.showCashbackPopups ? 'block' : 'none',
        zIndex: 2147483648,
        position: 'fixed',
        height: '100%',
        width: '100%',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      }}
      >
        <CashbackPopupFrame />
      </Box>
      <Box sx={{
        display: UiStore.checkoutExampleOpen ? 'block' : 'none',
        zIndex: 2147483648,
        position: 'fixed',
        height: '100%',
        width: '100%',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      }}
      >
        <CashbackExampleFrame />
      </Box>
    </Grid>
  );
});

export default Layout;
